import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, ArrowRight, ChevronRight } from 'lucide-react';

const CustomNavbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeFlyout, setActiveFlyout] = useState(null);
  const [currentPage, setCurrentPage] = useState('home');
  const flyoutTimer = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-dropdown')) {
        setActiveDropdown(null);
        setActiveFlyout(null);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/' || path === '/home') setCurrentPage('home');
    else if (path.includes('/about')) setCurrentPage('about');
    else if (path.includes('/team/board')) setCurrentPage('executive-team');
    else if (path.includes('/team/executive')) setCurrentPage('leadership-team');
    else if (path.includes('/support_us')) setCurrentPage('supportus');
    else if (path.includes('/campaign')) setCurrentPage('campaign');
    else if (path.includes('/impact')) setCurrentPage('impact');
    else if (path.includes('/programs')) setCurrentPage('programs');
    else if (path.includes('/projects/one-outreach')) setCurrentPage('one-outreach');
    else if (path.includes('/projects/rghd-project')) setCurrentPage('rghd-project');
    else if (path.includes('/projects/sgbv')) setCurrentPage('sgbv');
    else if (path.includes('/projects/ncd-hiv')) setCurrentPage('ncd-hiv');
    else if (path.includes('/projects/rcasr')) setCurrentPage('rcasr');
    else if (path.includes('/projects/rhasmp')) setCurrentPage('rhasmp');
    else if (path.includes('/projects/rakai-t1d')) setCurrentPage('rakai-t1d');
    else if (path.includes('/projects/ovc')) setCurrentPage('ovc');
    else if (path.includes('/projects')) setCurrentPage('projects');
    else if (path.includes('/contact')) setCurrentPage('contact');
  }, []);

  const handleNavClick = (pageId, path = null) => {
    setCurrentPage(pageId);
    setMobileMenuOpen(false);
    setActiveDropdown(null);
    setActiveFlyout(null);
    if (path && window.history) {
      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDropdownToggle = (dropdownId, event) => {
    event.stopPropagation();
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
    setActiveFlyout(null);
  };

  const handleFlyoutEnter = (flyoutId) => {
    clearTimeout(flyoutTimer.current);
    setActiveFlyout(flyoutId);
  };

  const handleFlyoutLeave = () => {
    flyoutTimer.current = setTimeout(() => setActiveFlyout(null), 150);
  };

  const handleFlyoutPanelEnter = () => {
    clearTimeout(flyoutTimer.current);
  };

  // navItems – "Our Team" now has a flyout config instead of a direct path
  const navItems = [
    { id: 'home', label: 'Home', path: '/' },
    {
      id: 'about',
      label: 'About Us',
      dropdown: [
        { id: 'about', label: 'Our Story', path: '/about' },
        {
          id: 'team',
          label: 'Our Team',
          flyout: [
            { id: 'executive-team', label: 'Board of Directors', path: '/team/board' },
            { id: 'leadership-team', label: 'Executive Team', path: '/team/executive' },
          ],
        },
        { id: 'impact', label: 'Our Impact', path: '/impact' },
      ],
    },
    {
      id: 'projects',
      label: 'Projects',
      dropdown: [
        { id: 'one-outreach', label: 'One Outreach for All', path: '/projects/one-outreach' },
        { id: 'sgbv', label: 'Combating SGBV', path: '/projects/sgbv' },
        { id: 'ncd-hiv', label: 'NCD/HIV Integration', path: '/projects/ncd-hiv' },
        { id: 'rghd-project', label: 'RGHD Project', path: '/projects/rghd-project' },
        { id: 'rcasr-project', label: 'Cancer Project', path: '/projects/rcasr' },
        { id: 'rhasmp-project', label: 'Hepatitis B Project', path: '/projects/rhasmp' },
        { id: 'rakai-t1d', label: 'Rakai T1D Project', path: '/projects/rakai-t1d' },
        { id: 'ovc', label: 'OVC Project', path: '/projects/ovc' },
      ],
    },
    { id: 'campaign', label: 'Campaign', path: '/campaign' },
    { id: 'supportus', label: 'Support Us', path: '/support_us' },
    { id: 'programs', label: 'Programs', path: '/programs' },
    { id: 'contact', label: 'Contact', path: '/contact' },
  ];

  // Check if a dropdown item (including flyout children) matches current page
  const isDropdownActive = (dropdown) =>
    dropdown.some((sub) =>
      sub.flyout
        ? sub.flyout.some((f) => f.id === currentPage)
        : sub.id === currentPage
    );

  return (
    <>
      <nav className={`professional-navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-wrapper">
          {/* Brand */}
          <div className="navbar-brand-section" onClick={() => handleNavClick('home', '/')}>
            <div className="logo-container">
              <div className="logo-image">
                <img
                  src="/images/logo.png"
                  alt="RACOHI Logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="logo-fallback"><span>R</span></div>
              </div>
            </div>
            <div className="organization-title">
              <div className="org-name">Rakai Community Health Initiative</div>
              <div className="org-tagline">Building Healthier Communities</div>
            </div>
          </div>

          {/* Desktop Nav */}
          <div className="desktop-navigation">
            <div className="nav-menu">
              {navItems.map((item) => (
                <div key={item.id} className="nav-item">
                  {item.dropdown ? (
                    <div className="nav-dropdown">
                      <button
                        className={`nav-button has-dropdown ${isDropdownActive(item.dropdown) ? 'active' : ''}`}
                        onClick={(e) => handleDropdownToggle(item.id, e)}
                      >
                        {item.label}
                        <ChevronDown size={16} className={`chevron ${activeDropdown === item.id ? 'rotated' : ''}`} />
                      </button>

                      <div className={`dropdown-panel ${activeDropdown === item.id ? 'visible' : ''}`}>
                        {item.dropdown.map((subItem) =>
                          subItem.flyout ? (
                            // Item with flyout
                            <div
                              key={subItem.id}
                              className="flyout-trigger-wrap"
                              onMouseEnter={() => handleFlyoutEnter(subItem.id)}
                              onMouseLeave={handleFlyoutLeave}
                            >
                              <button
                                className={`dropdown-link flyout-trigger ${
                                  subItem.flyout.some((f) => f.id === currentPage) ? 'current' : ''
                                }`}
                              >
                                {subItem.label}
                                <ChevronRight size={14} className="link-arrow" />
                              </button>

                              {/* Flyout panel */}
                              <div
                                className={`flyout-panel ${activeFlyout === subItem.id ? 'visible' : ''}`}
                                onMouseEnter={handleFlyoutPanelEnter}
                                onMouseLeave={handleFlyoutLeave}
                              >
                                {subItem.flyout.map((flyItem) => (
                                  <button
                                    key={flyItem.id}
                                    className={`dropdown-link ${currentPage === flyItem.id ? 'current' : ''}`}
                                    onClick={() => handleNavClick(flyItem.id, flyItem.path)}
                                  >
                                    {flyItem.label}
                                    <ArrowRight size={14} className="link-arrow" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <button
                              key={subItem.id}
                              className={`dropdown-link ${currentPage === subItem.id ? 'current' : ''}`}
                              onClick={() => handleNavClick(subItem.id, subItem.path)}
                            >
                              {subItem.label}
                              <ArrowRight size={14} className="link-arrow" />
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  ) : (
                    <button
                      className={`nav-button ${currentPage === item.id ? 'active' : ''}`}
                      onClick={() => handleNavClick(item.id, item.path)}
                    >
                      {item.label}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button className="cta-btn" onClick={() => handleNavClick('contact', '/contact')}>
              Get Involved
            </button>
          </div>

          {/* Mobile Toggle */}
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-content">
            {navItems.map((item) => (
              <div key={item.id} className="mobile-item">
                {item.dropdown ? (
                  <div className="mobile-dropdown">
                    <button
                      className={`mobile-button dropdown-trigger ${isDropdownActive(item.dropdown) ? 'active' : ''}`}
                      onClick={(e) => handleDropdownToggle(item.id, e)}
                    >
                      {item.label}
                      <ChevronDown size={16} className={`chevron ${activeDropdown === item.id ? 'rotated' : ''}`} />
                    </button>
                    <div className={`mobile-submenu ${activeDropdown === item.id ? 'expanded' : ''}`}>
                      {item.dropdown.map((subItem) =>
                        subItem.flyout ? (
                          <div key={subItem.id}>
                            <button
                              className="mobile-sublink mobile-flyout-label"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveFlyout(activeFlyout === subItem.id ? null : subItem.id);
                              }}
                            >
                              {subItem.label}
                              <ChevronDown
                                size={14}
                                className={`chevron ${activeFlyout === subItem.id ? 'rotated' : ''}`}
                              />
                            </button>
                            <div className={`mobile-nested-submenu ${activeFlyout === subItem.id ? 'expanded' : ''}`}>
                              {subItem.flyout.map((flyItem) => (
                                <button
                                  key={flyItem.id}
                                  className={`mobile-sublink mobile-nested-link ${currentPage === flyItem.id ? 'current' : ''}`}
                                  onClick={() => handleNavClick(flyItem.id, flyItem.path)}
                                >
                                  › {flyItem.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <button
                            key={subItem.id}
                            className={`mobile-sublink ${currentPage === subItem.id ? 'current' : ''}`}
                            onClick={() => handleNavClick(subItem.id, subItem.path)}
                          >
                            {subItem.label}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ) : (
                  <button
                    className={`mobile-button ${currentPage === item.id ? 'active' : ''}`}
                    onClick={() => handleNavClick(item.id, item.path)}
                  >
                    {item.label}
                  </button>
                )}
              </div>
            ))}
            <button className="mobile-cta" onClick={() => handleNavClick('contact', '/contact')}>
              Get Involved
            </button>
          </div>
        </div>
      </nav>

      <style jsx>{`
        :root {
          --primary-blue: #2563eb;
          --secondary-blue: #1e40af;
          --accent-teal: #06b6d4;
          --accent-orange: #f59e0b;
          --text-dark: #1f2937;
          --text-gray: #6b7280;
          --white: #ffffff;
          --light-bg: #f8fafc;
          --border-light: rgba(0, 0, 0, 0.06);
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }

        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif; }

        .professional-navbar {
          position: fixed; top: 0; left: 0; right: 0;
          height: 80px;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid var(--border-light);
          z-index: 1000;
          transition: all 0.3s ease;
        }

        .professional-navbar.scrolled {
          height: 70px;
          background: rgba(255,255,255,0.98);
          box-shadow: 0 4px 24px rgba(0,0,0,0.08);
        }

        .navbar-wrapper {
          max-width: 1400px; margin: 0 auto; height: 100%;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 2rem;
        }

        .navbar-brand-section {
          display: flex; align-items: center; gap: 16px;
          cursor: pointer; transition: transform 0.2s ease;
          flex: 0 0 auto; margin-right: 2rem;
        }
        .navbar-brand-section:hover { transform: scale(1.02); }

        .logo-image {
          position: relative; width: 48px; height: 48px; transition: all 0.3s ease;
        }
        .scrolled .logo-image { width: 40px; height: 40px; }

        .logo-image img {
          width: 100%; height: 100%; object-fit: cover;
          border-radius: 50%; border: 2px solid var(--primary-blue);
        }

        .logo-fallback {
          display: none; width: 100%; height: 100%;
          background: linear-gradient(135deg, var(--primary-blue), var(--accent-teal));
          border-radius: 50%; align-items: center; justify-content: center;
          color: white; font-weight: 700; font-size: 1.5rem;
          border: 2px solid var(--primary-blue);
        }

        .organization-title { display: flex; flex-direction: column; gap: 2px; }
        .org-name {
          font-size: 20px; font-weight: 700; color: var(--primary-blue);
          line-height: 1.1; transition: font-size 0.3s ease; white-space: nowrap;
        }
        .scrolled .org-name { font-size: 18px; }
        .org-tagline {
          font-size: 12px; color: var(--accent-teal); font-weight: 500;
          text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap;
        }

        .desktop-navigation { display: flex; align-items: center; gap: 2rem; flex: 0 0 auto; }
        .nav-menu { display: flex; align-items: center; gap: 8px; }
        .nav-item { position: relative; }

        .nav-button {
          display: flex; align-items: center; gap: 6px;
          padding: 12px 16px; background: none; border: none;
          color: var(--text-dark); font-size: 15px; font-weight: 500;
          border-radius: 8px; cursor: pointer; transition: all 0.2s ease; white-space: nowrap;
        }
        .nav-button:hover { background: var(--light-bg); color: var(--primary-blue); }
        .nav-button.active {
          background: rgba(37,99,235,0.08); color: var(--primary-blue); font-weight: 600;
        }

        .chevron { transition: transform 0.2s ease; }
        .chevron.rotated { transform: rotate(180deg); }

        /* Main dropdown panel */
        .dropdown-panel {
          position: absolute; top: calc(100% + 12px); left: 50%;
          transform: translateX(-50%) translateY(-8px);
          width: 200px; background: white; border-radius: 12px;
          box-shadow: 0 12px 48px rgba(0,0,0,0.12);
          border: 1px solid var(--border-light);
          padding: 8px; opacity: 0; visibility: hidden;
          transition: all 0.3s cubic-bezier(0.4,0,0.2,1); z-index: 100;
        }
        .dropdown-panel.visible {
          opacity: 1; visibility: visible;
          transform: translateX(-50%) translateY(0);
        }

        .dropdown-link {
          display: flex; align-items: center; justify-content: space-between;
          width: 100%; padding: 12px; background: none; border: none;
          border-radius: 6px; color: var(--text-dark); font-size: 14px;
          font-weight: 500; cursor: pointer; transition: all 0.2s ease; text-align: left;
        }
        .dropdown-link:hover { background: var(--light-bg); color: var(--primary-blue); }
        .dropdown-link.current {
          background: rgba(37,99,235,0.08); color: var(--primary-blue); font-weight: 600;
        }

        .link-arrow { opacity: 0; transition: all 0.2s ease; }
        .dropdown-link:hover .link-arrow { opacity: 1; transform: translateX(2px); }

        /* Flyout */
        .flyout-trigger-wrap { position: relative; }

        .flyout-trigger .link-arrow { opacity: 0.5; }
        .flyout-trigger:hover .link-arrow { opacity: 1; transform: translateX(0); }
        .flyout-trigger-wrap:hover .flyout-trigger { background: var(--light-bg); color: var(--primary-blue); }

        .flyout-panel {
          position: absolute;
          top: 0;
          left: calc(100% + 8px);
          width: 190px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 12px 48px rgba(0,0,0,0.14);
          border: 1px solid var(--border-light);
          padding: 8px;
          opacity: 0; visibility: hidden;
          transform: translateX(-6px);
          transition: all 0.22s cubic-bezier(0.4,0,0.2,1);
          z-index: 101;
        }
        .flyout-panel.visible {
          opacity: 1; visibility: visible; transform: translateX(0);
        }

        /* CTA */
        .cta-btn {
          padding: 12px 24px; background: var(--accent-orange); color: white;
          border: none; border-radius: 8px; font-size: 14px; font-weight: 600;
          cursor: pointer; transition: all 0.2s ease; white-space: nowrap;
        }
        .cta-btn:hover {
          background: #d97706; transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(245,158,11,0.3);
        }

        /* Mobile */
        .mobile-menu-btn {
          display: none; align-items: center; justify-content: center;
          width: 40px; height: 40px; background: var(--light-bg);
          border: none; border-radius: 6px; color: var(--text-dark);
          cursor: pointer; transition: all 0.2s ease;
        }
        .mobile-menu-btn:hover { background: rgba(37,99,235,0.08); color: var(--primary-blue); }

        .mobile-menu {
          position: absolute; top: 100%; left: 0; right: 0;
          background: white; border-bottom: 1px solid var(--border-light);
          max-height: 0; overflow: hidden; transition: all 0.3s ease;
        }
        .mobile-menu.open {
          max-height: 600px; box-shadow: 0 8px 24px rgba(0,0,0,0.1);
          overflow-y: auto;
        }

        .mobile-content { padding: 1rem; }
        .mobile-item { margin-bottom: 4px; }

        .mobile-button {
          display: flex; align-items: center; justify-content: space-between;
          width: 100%; padding: 16px 0; background: none; border: none;
          color: var(--text-dark); font-size: 16px; font-weight: 500;
          cursor: pointer; transition: color 0.2s ease; text-align: left;
        }
        .mobile-button:hover, .mobile-button.active { color: var(--primary-blue); }

        .mobile-submenu { max-height: 0; overflow: hidden; transition: all 0.3s ease; }
        .mobile-submenu.expanded {
          max-height: 400px; padding-left: 1rem; margin-top: 8px;
        }

        .mobile-sublink {
          display: flex; align-items: center; justify-content: space-between;
          width: 100%; padding: 12px 0; background: none; border: none;
          color: var(--text-gray); font-size: 15px; font-weight: 500;
          cursor: pointer; transition: color 0.2s ease; text-align: left;
        }
        .mobile-sublink:hover, .mobile-sublink.current { color: var(--primary-blue); }

        .mobile-flyout-label { font-weight: 600; color: var(--text-dark); }

        .mobile-nested-submenu { max-height: 0; overflow: hidden; transition: all 0.3s ease; }
        .mobile-nested-submenu.expanded {
          max-height: 200px; padding-left: 1rem; margin-top: 4px;
        }

        .mobile-nested-link { font-size: 14px; padding: 10px 0; }

        .mobile-cta {
          width: 100%; padding: 16px; background: var(--accent-orange);
          color: white; border: none; border-radius: 8px; font-size: 16px;
          font-weight: 600; cursor: pointer; margin-top: 1rem; transition: background 0.2s ease;
        }
        .mobile-cta:hover { background: #d97706; }

        /* Responsive */
        @media (max-width: 1024px) {
          .navbar-wrapper { padding: 0 1.5rem; }
          .navbar-brand-section { margin-right: 2rem; }
          .nav-menu { gap: 4px; }
          .nav-button { padding: 10px 12px; font-size: 14px; }
          .org-name { font-size: 16px; }
          .scrolled .org-name { font-size: 14px; }
        }
        @media (max-width: 900px) { .org-tagline { display: none; } }
        @media (max-width: 768px) {
          .desktop-navigation { display: none; }
          .mobile-menu-btn { display: flex; }
          .navbar-wrapper { padding: 0 1rem; }
          .navbar-brand-section { margin-right: 0; }
          .org-name { font-size: 16px; }
          .logo-image { width: 42px; height: 42px; }
        }
        @media (max-width: 480px) {
          .org-name { font-size: 14px; }
          .logo-image { width: 36px; height: 36px; }
          .navbar-brand-section { gap: 12px; }
        }

        .nav-button:focus, .dropdown-link:focus, .cta-btn:focus,
        .mobile-menu-btn:focus, .navbar-brand-section:focus {
          outline: 2px solid var(--accent-orange); outline-offset: 2px;
        }
      `}</style>
    </>
  );
};

export default CustomNavbar;